import React, { useState, useEffect } from 'react';
import { styleMatcherService, StyleMatcherSession, StyleMatcherResults } from '../services/styleMatcher';
import { customerService } from '../services/customer';

interface StyleMatcherDetailsProps {
  customerId: string;
  onClose: () => void;
}

const StyleMatcherDetails: React.FC<StyleMatcherDetailsProps> = ({ customerId, onClose }) => {
  const [customer, setCustomer] = useState<any>(null);
  const [sessions, setSessions] = useState<StyleMatcherSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<StyleMatcherSession | null>(null);
  const [sessionResults, setSessionResults] = useState<StyleMatcherResults | null>(null);
  const [sessionSwipes, setSessionSwipes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'sessions' | 'preferences' | 'swipes'>('overview');

  useEffect(() => {
    loadCustomerData();
  }, [customerId]);

  useEffect(() => {
    if (selectedSession) {
      loadSessionResults(selectedSession.id);
      loadSessionSwipes(selectedSession.id);
    }
  }, [selectedSession]);

  const loadCustomerData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load customer details
      const customerData = await customerService.getCustomer(customerId);
      setCustomer(customerData);

      // Load sessions
      const sessionsData = await styleMatcherService.getCustomerSessions(customerId);
      setSessions(sessionsData);

      // Select the most recent session by default
      if (sessionsData.length > 0) {
        setSelectedSession(sessionsData[0]);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load customer data');
    } finally {
      setLoading(false);
    }
  };

  const loadSessionResults = async (sessionId: string) => {
    try {
      const results = await styleMatcherService.getSessionResults(customerId, sessionId);
      setSessionResults(results);
    } catch (err: any) {
      console.error('Failed to load session results:', err);
    }
  };

  const loadSessionSwipes = async (sessionId: string) => {
    try {
      const swipes = await styleMatcherService.getSessionSwipes(sessionId);
      setSessionSwipes(swipes);
    } catch (err: any) {
      console.error('Failed to load session swipes:', err);
    }
  };

  const handleClickOutside = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50" onClick={handleClickOutside}>
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Style Matcher Details
              </h2>
              {customer && (
                <p className="mt-1 text-sm text-gray-600">
                  {customer.name} • {customer.email}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {['overview', 'sessions', 'preferences', 'swipes'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`${
                  activeTab === tab
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-lg text-gray-600">Loading...</div>
            </div>
          ) : error ? (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && sessionResults && (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="text-sm text-gray-500">Total Swipes</div>
                      <div className="text-2xl font-semibold">{sessionResults.totalSwipes}</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="text-sm text-gray-500">Likes</div>
                      <div className="text-2xl font-semibold text-green-600">{sessionResults.likes}</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="text-sm text-gray-500">Passes</div>
                      <div className="text-2xl font-semibold text-red-600">{sessionResults.passes}</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="text-sm text-gray-500">Skips</div>
                      <div className="text-2xl font-semibold text-gray-600">{sessionResults.skips}</div>
                    </div>
                  </div>

                  {/* Engagement Rate */}
                  {sessionResults.totalSwipes > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Engagement Rate</h3>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Like Rate</span>
                            <span>{Math.round((sessionResults.likes / sessionResults.totalSwipes) * 100)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-600 h-2 rounded-full" 
                              style={{ width: `${(sessionResults.likes / sessionResults.totalSwipes) * 100}%` }}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Pass Rate</span>
                            <span>{Math.round((sessionResults.passes / sessionResults.totalSwipes) * 100)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-red-600 h-2 rounded-full" 
                              style={{ width: `${(sessionResults.passes / sessionResults.totalSwipes) * 100}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'sessions' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Session History</h3>
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Started
                          </th>
                          <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Status
                          </th>
                          <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Total Swipes
                          </th>
                          <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Likes
                          </th>
                          <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Passes
                          </th>
                          <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Skips
                          </th>
                          <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                            <span className="sr-only">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {sessions.map((session) => (
                          <tr key={session.id} className={selectedSession?.id === session.id ? 'bg-indigo-50' : ''}>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                              {new Date(session.startedAt).toLocaleString()}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                              {session.isActive ? (
                                <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800">
                                  Active
                                </span>
                              ) : (
                                <span className="inline-flex rounded-full bg-gray-100 px-2 text-xs font-semibold leading-5 text-gray-800">
                                  Completed
                                </span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {session.totalSwipes}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {session.likesCount}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {session.passesCount}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {session.skipsCount}
                            </td>
                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              <button
                                onClick={() => setSelectedSession(session)}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'preferences' && sessionResults && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Category Preferences</h3>
                  
                  {Object.keys(sessionResults.categoryPreferences).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(sessionResults.categoryPreferences)
                        .sort(([, a], [, b]) => b - a)
                        .map(([category, percentage]) => (
                          <div key={category}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="capitalize">{category}</span>
                              <span>{percentage}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-indigo-600 h-2 rounded-full" 
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No category preferences available yet.</p>
                  )}

                  {sessionResults.recommendedDresses && sessionResults.recommendedDresses.length > 0 && (
                    <>
                      <h3 className="text-lg font-medium text-gray-900 mt-8">Recommended Dresses</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {sessionResults.recommendedDresses.map((dress) => (
                          <div key={dress.id} className="border border-gray-200 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900">{dress.name}</h4>
                            <p className="text-sm text-gray-600">{dress.designer}</p>
                            <p className="text-sm text-gray-500 mt-1">${dress.price}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'swipes' && sessionSwipes.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Swipe History</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sessionSwipes.map((swipe) => (
                      <div 
                        key={swipe.id} 
                        className={`border rounded-lg p-4 ${
                          swipe.action === 'like' 
                            ? 'border-green-200 bg-green-50' 
                            : swipe.action === 'pass'
                            ? 'border-red-200 bg-red-50'
                            : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium text-gray-900">{swipe.dress.name}</h4>
                          <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                            swipe.action === 'like'
                              ? 'bg-green-100 text-green-800'
                              : swipe.action === 'pass'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {swipe.action === 'like' ? '👍 Liked' : swipe.action === 'pass' ? '👎 Passed' : '⏭️ Skipped'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{swipe.dress.designer}</p>
                        <p className="text-sm text-gray-500">${swipe.dress.price}</p>
                        {swipe.dress.categories.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {swipe.dress.categories.map((category: string) => (
                              <span key={category} className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                                {category}
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(swipe.swipedAt).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StyleMatcherDetails;